<template>
  <div class="sv-page">

    <!-- ─── Hero ───────────────────────────────────────── -->
    <section class="sv-hero">
      <div class="sv-hero__bg" aria-hidden="true">
        <img src="./../../../assets/home_main_section_bg.png" alt="" />
      </div>
      <div class="sv-hero__inner sv-container">
        <div class="sv-hero__text">
          <p class="sv-hero__eyebrow">Secret Vault</p>
          <h1 class="sv-hero__title">{{ $t("message.home_main_info1") }}<br>{{ $t("message.home_main_info2") }}</h1>
          <p class="sv-hero__sub">{{ $t("message.home_sub_info1") }} {{ $t("message.home_sub_info2") }}</p>
          <div class="sv-hero__actions">
            <a class="sv-btn sv-btn--primary" href="https://itunes.apple.com/us/app/id1347647301?mt=8">
              <img class="sv-btn__icon" src="./../../../assets/icon_apple.png" alt="">
              <span>Install on App Store</span>
            </a>
          </div>
        </div>
        <div class="sv-hero__visual">
          <img src="./../../../assets/lock_screen.png" alt="Secret Vault app lock screen interface showing secure photo vault" class="sv-hero__phone" />
        </div>
      </div>
    </section>

    <!-- ─── Features ────────────────────────────────────── -->
    <section class="sv-features" aria-label="Secret Vault Features">
      <div class="sv-container">
        <h2 class="sv-section-title">{{ $t("message.home_features_title") }}</h2>
        <p class="sv-section-subtitle">{{ $t("message.home_features_subtitle") }}</p>
        <div class="sv-features__grid">
          <article class="sv-card sv-card--coral">
            <div class="sv-card__header">
              <span class="sv-card__icon">🔒</span>
              <h3 class="sv-card__title">{{ $t("message.home_section2_title1") }}</h3>
            </div>
            <p class="sv-card__body">{{ $t("message.home_section2_content1") }}</p>
          </article>
          <article class="sv-card sv-card--teal">
            <div class="sv-card__header">
              <span class="sv-card__icon">📤</span>
              <h3 class="sv-card__title">{{ $t("message.home_section2_title2") }}</h3>
            </div>
            <p class="sv-card__body">{{ $t("message.home_section2_content2") }}</p>
          </article>
          <article class="sv-card sv-card--rose">
            <div class="sv-card__header">
              <span class="sv-card__icon">📷</span>
              <h3 class="sv-card__title">{{ $t("message.home_section2_title3") }}</h3>
            </div>
            <p class="sv-card__body">{{ $t("message.home_section2_content3") }}</p>
          </article>
          <article class="sv-card sv-card--orange">
            <div class="sv-card__header">
              <span class="sv-card__icon">🎭</span>
              <h3 class="sv-card__title">{{ $t("message.home_section2_title4") }}</h3>
            </div>
            <p class="sv-card__body">{{ $t("message.home_section2_content4") }}</p>
          </article>
          <article class="sv-card sv-card--blue">
            <div class="sv-card__header">
              <span class="sv-card__icon">☁️</span>
              <h3 class="sv-card__title">{{ $t("message.home_section2_title5") }}</h3>
            </div>
            <p class="sv-card__body" v-html='$t("message.home_section2_content5")'></p>
          </article>
          <article class="sv-card sv-card--pink">
            <div class="sv-card__header">
              <span class="sv-card__icon">🎨</span>
              <h3 class="sv-card__title">{{ $t("message.home_section2_title6") }}</h3>
            </div>
            <p class="sv-card__body">{{ $t("message.home_section2_content6") }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- ─── FAQ CTA ──────────────────────────────────────── -->
    <section class="sv-faq-cta">
      <div class="sv-container sv-faq-cta__inner">
        <div class="sv-faq-cta__badge">FAQ</div>
        <h2 class="sv-faq-cta__title">{{ $t("message.faq_title") }}</h2>
        <p class="sv-faq-cta__desc">{{ $t("message.home_faq_description") }}</p>
        <router-link to="/faq" class="sv-btn sv-btn--outline">
          {{ $t("message.home_faq_link_text") }}
          <span class="sv-btn__arrow" aria-hidden="true">→</span>
        </router-link>
      </div>
    </section>

    <!-- ─── Screenshots ──────────────────────────────────── -->
    <section class="sv-screenshots">
      <div class="sv-container">
        <h2 class="sv-section-title">Screenshots</h2>
        <div class="sv-screenshots__grid">
          <img src="./../../../assets/screen_shot1.png" alt="Secret Vault photo vault interface screenshot" />
          <img src="./../../../assets/screen_shot2.png" alt="Secret Vault secure album management screenshot" />
          <img src="./../../../assets/screen_shot3.png" alt="Secret Vault privacy protection features screenshot" />
          <img src="./../../../assets/screen_shot4.png" alt="Secret Vault encrypted photo storage screenshot" />
        </div>
      </div>
    </section>

    <!-- ─── Footer ───────────────────────────────────────── -->
    <footer class="sv-footer">
      <div class="sv-container sv-footer__inner">
        <p v-html='$t("message.home_footer_content1")' class="sv-footer__terms" />
        <p class="sv-footer__copy">{{ $t("message.home_footer_content2") }}</p>
        <nav class="sv-footer__links" aria-label="Footer navigation">
          <router-link to="/faq">{{ $t("message.faq_title") }}</router-link>
          <span aria-hidden="true">·</span>
          <router-link to="/terms/privacy">{{ $t("message.privacy_policy_title") }}</router-link>
          <span aria-hidden="true">·</span>
          <router-link to="/terms/service">{{ $t("message.conditions_of_service_title") }}</router-link>
        </nav>
        <p v-if="isChineseSimplified" class="sv-footer__icp">
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">沪ICP备2024062474号</a>
        </p>
      </div>
    </footer>

  </div>
</template>

<script>
export default {
  computed: {
    isChineseSimplified() {
      return this.$i18n.locale === 'zhCHS' || this.$i18n.locale === 'zh-Hans'
    }
  }
}
</script>

<style scoped lang="scss">
// ── Design tokens ──────────────────────────────────────────────
$c-near-black:   #1c1c1e;
$c-slate:        #555a6a;
$c-placeholder:  #a5a8b5;
$c-border:       #c7cad5;
$c-ring:         rgb(224, 226, 232);
$c-blue:         #5b76fe;
$c-blue-pressed: #2a41b6;
$c-white:        #ffffff;

// Pastel accents
$c-coral:  #ffc6c6;
$c-rose:   #ffd8f4;
$c-teal:   #c3faf5;
$c-orange: #ffe6cd;
$c-blue-l: #dce4ff;
$c-pink:   #fde0f0;

// Typography
$font-display: 'Plus Jakarta Sans', 'DM Sans', system-ui, -apple-system, sans-serif;
$font-body:    'Noto Sans', system-ui, -apple-system, sans-serif;

// ── Base ───────────────────────────────────────────────────────
.sv-page {
  background: $c-white;
  color: $c-near-black;
  font-family: $font-body;
  min-height: 100vh;
  overflow-x: hidden;
}

.sv-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

// ── Buttons ────────────────────────────────────────────────────
.sv-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: $font-display;
  font-size: 17.5px;
  font-weight: 700;
  letter-spacing: 0.175px;
  line-height: 1.29;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  white-space: nowrap;

  &--primary {
    background: $c-blue;
    color: $c-white;
    border: 1.5px solid $c-blue;

    &:hover {
      background: $c-blue-pressed;
      border-color: $c-blue-pressed;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba($c-blue, 0.35);
    }

    &:active { transform: translateY(0); }
  }

  &--outline {
    background: $c-white;
    color: $c-near-black;
    border: 1px solid $c-border;

    &:hover {
      border-color: $c-blue;
      color: $c-blue;
      box-shadow: rgb(224,226,232) 0px 0px 0px 1px;
      transform: translateY(-1px);

      .sv-btn__arrow { transform: translateX(4px); }
    }

    &:active { transform: translateY(0); }
  }

  &__icon {
    height: 1.2em;
    width: auto;
  }

  &__arrow {
    display: inline-block;
    margin-left: 2px;
    transition: transform 0.2s ease;
  }
}

// ── Shared section headings ────────────────────────────────────
.sv-section-title {
  font-family: $font-display;
  font-size: 48px;
  font-weight: 700;
  color: $c-near-black;
  text-align: center;
  margin: 0 0 16px;
  line-height: 1.15;
  letter-spacing: -1.44px;

  @media (max-width: 768px) {
    font-size: 32px;
    letter-spacing: -0.96px;
  }
}

.sv-section-subtitle {
  font-family: $font-body;
  font-size: 18px;
  color: $c-slate;
  text-align: center;
  margin: 0 0 56px;
  line-height: 1.45;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 40px;
  }
}

// ── Hero ───────────────────────────────────────────────────────
.sv-hero {
  position: relative;
  min-height: 580px;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: linear-gradient(135deg, #1a2340 0%, #2a41b6 60%, #5b76fe 100%);

  &__bg {
    position: absolute;
    inset: 0;
    z-index: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0.18;
    }
  }

  &__inner {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 48px;
    padding-top: 64px;
    padding-bottom: 64px;
    width: 100%;

    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
      gap: 32px;
      padding-top: 48px;
      padding-bottom: 48px;
    }
  }

  &__text {
    flex: 1;
    max-width: 580px;

    @media (max-width: 768px) {
      max-width: 100%;
    }
  }

  &__eyebrow {
    font-family: $font-display;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba($c-white, 0.65);
    margin: 0 0 20px;
  }

  &__title {
    font-family: $font-display;
    font-size: 56px;
    font-weight: 700;
    color: $c-white;
    margin: 0 0 20px;
    line-height: 1.15;
    letter-spacing: -1.68px;

    @media (max-width: 896px) { font-size: 44px; }
    @media (max-width: 576px) { font-size: 36px; }
  }

  &__sub {
    font-family: $font-body;
    font-size: 18px;
    color: rgba($c-white, 0.80);
    line-height: 1.55;
    margin: 0 0 36px;
  }

  &__actions {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      justify-content: center;
    }
  }

  &__visual {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
  }

  &__phone {
    width: 240px;
    filter: drop-shadow(0 32px 48px rgba(0,0,0,0.45));
    border-radius: 24px;

    @media (max-width: 1024px) { width: 200px; }
    @media (max-width: 768px) { width: 180px; }
  }
}

// ── Features ───────────────────────────────────────────────────
.sv-features {
  padding: 96px 0;
  background: $c-white;

  @media (max-width: 768px) { padding: 64px 0; }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 576px)  { grid-template-columns: 1fr; }
  }
}

.sv-card {
  border-radius: 20px;
  padding: 36px 28px;
  box-shadow: rgb(224,226,232) 0px 0px 0px 1px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: rgb(224,226,232) 0px 0px 0px 1px, 0 16px 40px rgba(0,0,0,0.09);
  }

  // Pastel → near-white gradients per accent
  &--coral  { background: linear-gradient(145deg, #ffe4e4 0%, #fff8f8 100%); }
  &--teal   { background: linear-gradient(145deg, #d6faf6 0%, #f4fffd 100%); }
  &--rose   { background: linear-gradient(145deg, #fce8f8 0%, #fef6fd 100%); }
  &--orange { background: linear-gradient(145deg, #ffeedd 0%, #fffaf5 100%); }
  &--blue   { background: linear-gradient(145deg, #e4eaff 0%, #f5f7ff 100%); }
  &--pink   { background: linear-gradient(145deg, #fde8f5 0%, #fef6fb 100%); }

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: nowrap;
  }

  &__icon {
    font-size: 28px;
    line-height: 1;
    flex-shrink: 0;
  }

  &__title {
    font-family: $font-display;
    font-size: 17px;
    font-weight: 700;
    color: $c-near-black;
    margin: 0;
    line-height: 1.3;
    letter-spacing: -0.3px;
    white-space: nowrap;
  }

  &__body {
    font-family: $font-body;
    font-size: 15px;
    color: $c-slate;
    line-height: 1.65;
    margin: 0;

    a {
      color: $c-blue;
      text-decoration: none;
      font-weight: 600;
      &:hover { text-decoration: underline; }
    }

    strong {
      font-weight: 600;
      color: $c-near-black;
    }
  }
}

// ── FAQ CTA ────────────────────────────────────────────────────
.sv-faq-cta {
  background: $c-teal;
  padding: 80px 0;

  @media (max-width: 768px) { padding: 56px 0; }

  &__inner {
    text-align: center;
  }

  &__badge {
    display: inline-block;
    font-family: $font-display;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #187574;
    background: rgba(#187574, 0.12);
    border-radius: 50px;
    padding: 5px 14px;
    margin-bottom: 20px;
  }

  &__title {
    font-family: $font-display;
    font-size: 40px;
    font-weight: 700;
    color: $c-near-black;
    margin: 0 0 16px;
    line-height: 1.15;
    letter-spacing: -1.2px;

    @media (max-width: 768px) { font-size: 28px; }
  }

  &__desc {
    font-family: $font-body;
    font-size: 18px;
    color: $c-slate;
    margin: 0 0 32px;
    line-height: 1.55;
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) { font-size: 16px; }
  }
}

// ── Screenshots ────────────────────────────────────────────────
.sv-screenshots {
  padding: 96px 0;
  background: #f4f5f7;

  @media (max-width: 768px) { padding: 64px 0; }

  &__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-top: 48px;

    @media (max-width: 896px)  { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    @media (max-width: 425px)  { grid-template-columns: repeat(2, 1fr); gap: 8px; }

    img {
      width: 100%;
      border-radius: 16px;
      box-shadow: rgb(224,226,232) 0px 0px 0px 1px, 0 4px 16px rgba(0,0,0,0.06);
      display: block;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-4px);
        box-shadow: rgb(224,226,232) 0px 0px 0px 1px, 0 12px 32px rgba(0,0,0,0.12);
      }
    }
  }
}

// ── Footer ─────────────────────────────────────────────────────
.sv-footer {
  background: $c-white;
  border-top: 1px solid #e9eaef;
  padding: 40px 0;

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
  }

  &__terms {
    font-family: $font-body;
    font-size: 13px;
    color: $c-slate;
    line-height: 1.65;
    margin: 0;

    a {
      color: $c-blue;
      text-decoration: none;
      font-weight: 600;
      &:hover { text-decoration: underline; }
    }
  }

  &__copy {
    font-family: $font-body;
    font-size: 12px;
    color: $c-placeholder;
    margin: 0;
  }

  &__links {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
    font-family: $font-body;
    font-size: 13px;

    a {
      color: $c-slate;
      text-decoration: none;
      transition: color 0.2s ease;
      &:hover { color: $c-blue; }
    }

    span {
      color: $c-placeholder;
      font-size: 10px;
    }
  }

  &__icp {
    font-size: 12px;
    color: $c-placeholder;
    margin: 0;

    a {
      color: $c-placeholder;
      text-decoration: none;
      &:hover { color: $c-slate; }
    }
  }
}
</style>
